import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { CONFIG, getRequiredConfig } from './config';
import {
  Alert as AntAlert,
  Button as AntButton,
  Card as AntCard,
  Col as AntCol,
  Collapse as AntCollapse,
  Divider as AntDivider,
  Input as AntInput,
  List as AntList,
  Modal as AntModal,
  Row as AntRow,
  Select as AntSelect,
  Space as AntSpace,
  Spin as AntSpin,
  Tag as AntTag,
  Typography as AntTypography,
} from 'antd';
import {
  Button as AMButton,
  Card as AMCard,
  Collapse as AMCollapse,
  Input as AMInput,
  List as AMList,
  Popup as AMPopup,
  SafeArea as AMSafeArea,
  Space as AMSpace,
  SpinLoading as AMSpin,
  Tag as AMTag,
} from 'antd-mobile';

const API_BASE = getRequiredConfig(CONFIG.LIFE_QUOTE_API_BASE, 'REACT_APP_LIFE_QUOTE_API_BASE');
const ENDPOINTS = {
  coverage: `${API_BASE}/coverage-amt-dropdown`,
  conditions: `${API_BASE}/medical-condition`,
  medications: `${API_BASE}/rx-dropdown`,
  criteria: `${API_BASE}/criteria`,
  quote: `${API_BASE}/get-quote`,
};

const LOGO_URL = getRequiredConfig(CONFIG.LIFE_QUOTE_LOGO_URL, 'REACT_APP_LIFE_QUOTE_LOGO_URL');

const STATE_OPTIONS = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
];

const DETAIL_FIELDS = [
  { label: 'Age', key: 'age' },
  { label: 'Minimum Coverage Amount', key: 'min_coverage_amt' },
  { label: 'Maximum Coverage Amount', key: 'max_coverage_amt' },
  { label: 'Form No', key: 'form_no' },
  { label: 'Policy Fee', key: 'policy_fee' },
  { label: 'Policy Fee Commissionable', key: 'policy_fee_commissionable' },
  { label: 'Term Illness/Accidental Death Rider', key: 'term_illness_accl_death_rider' },
  { label: 'Accelerated Death/Confined Care Rider', key: 'accl_death_confined_care_rider' },
  { label: 'Accelerated Death Terminal Illness Rider', key: 'accl_death_term_illness_rider' },
  { label: 'Nursing Home Confinement Care Rider', key: 'nursing_confine_rider' },
  { label: 'Accidental Death Benefit Rider', key: 'accid_death_rider' },
  { label: 'Grandchild Rider', key: 'grandchild_rider' },
  { label: 'Nursing Home Waive Premium Rider', key: 'nursing_home_waive_prem_rider' },
  { label: "Children's Insurance Agreement", key: 'childrens_ins_agreement' },
  { label: 'Accident Death Agreement', key: 'accident_death_agreement' },
  { label: 'Accelerated Death Benefit with Nursing Home Benefit', key: 'accl_death_benefit_w_nursing_benefit' },
  { label: 'Accelerated Death Benefit', key: 'accl_death_benefit' },
  { label: 'Concierge Planning', key: 'concierge_planning' },
  { label: 'Death Benefit', key: 'death_benefit' },
  { label: 'Plan Description', key: 'plan_description' },
  { label: 'Phone Interview Requirement', key: 'phone_interview_requirement' },
];

const defaultFormState = {
  coverageAmount: '',
  desiredPremium: '',
  gender: '',
  state: '',
  dob: '',
  heightFeet: '',
  heightInches: '',
  weight: '',
  nicotineUse: '',
};

const normalizeValue = (value) => {
  if (value === null || value === undefined) return null;
  const raw = String(value).trim();
  if (!raw || raw === 'N/A') return null;
  if (raw === '1') return 'Yes';
  if (raw === '0') return 'No';
  return value;
};

const sanitizeInches = (value) => {
  const raw = String(value || '').replace(/\D/g, '');
  if (!raw) return '';
  let trimmed = raw.slice(0, 2);
  if (trimmed.length > 1 && trimmed[0] === '0') {
    trimmed = trimmed.slice(1);
  }
  const num = Number(trimmed);
  if (!Number.isFinite(num)) return '';
  return String(Math.min(num, 11));
};

const MobileSelect = ({ label, value, onChange, options, placeholder, disabled }) => {
  const [open, setOpen] = useState(false);
  const selected = options.find((opt) => String(opt.value) === String(value));
  const displayValue = selected ? selected.label : '';
  const extra = displayValue ? (
    <span>{displayValue}</span>
  ) : (
    <span style={{ color: '#9aa3af' }}>{placeholder}</span>
  );

  return (
    <>
      <AMList.Item
        extra={extra}
        onClick={() => {
          if (!disabled) setOpen(true);
        }}
        clickable
      >
        <span style={{ fontWeight: 600 }}>{label}</span>
      </AMList.Item>
        <AMPopup
        visible={open}
        onMaskClick={() => setOpen(false)}
        bodyStyle={{ maxHeight: '70vh', overflow: 'hidden' }}
      >
        <AMSafeArea position="top" />
        <div style={{ padding: '12px 16px', fontWeight: 700 }}>{label}</div>
        <div style={{ maxHeight: '52vh', overflowY: 'auto' }}>
          <AMList>
            {options.map((opt) => (
              <AMList.Item
                key={String(opt.value)}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                clickable
              >
                {opt.label}
              </AMList.Item>
            ))}
          </AMList>
        </div>
        <div style={{ padding: 12 }}>
          <AMButton block onClick={() => setOpen(false)}>
            Close
          </AMButton>
        </div>
        <AMSafeArea position="bottom" />
      </AMPopup>
    </>
  );
};

const LifeQuoteTool = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [form, setForm] = useState(defaultFormState);
  const [coverageOptions, setCoverageOptions] = useState([]);
  const [conditionOptions, setConditionOptions] = useState([]);
  const [medicationOptions, setMedicationOptions] = useState([]);
  const [pendingCondition, setPendingCondition] = useState('');
  const [pendingMedication, setPendingMedication] = useState('');
  const [conditions, setConditions] = useState([]);
  const [medications, setMedications] = useState([]);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [premiumData, setPremiumData] = useState([]);
  const [missingFields, setMissingFields] = useState([]);
  const [modal, setModal] = useState({ type: null, message: '' });
  const [errorMessage, setErrorMessage] = useState('');

  const showModal = useCallback((type, message) => {
    setModal({ type, message });
  }, []);

  const closeModal = useCallback(() => {
    setModal({ type: null, message: '' });
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchOptions = async () => {
      try {
        const [coverageRes, conditionRes, medicationRes] = await Promise.all([
          fetch(ENDPOINTS.coverage),
          fetch(ENDPOINTS.conditions),
          fetch(ENDPOINTS.medications),
        ]);
        const [coverageJson, conditionJson, medicationJson] = await Promise.all([
          coverageRes.json(),
          conditionRes.json(),
          medicationRes.json(),
        ]);
        if (cancelled) return;
        const coverage = Array.isArray(coverageJson?.coverageAmounts)
          ? coverageJson.coverageAmounts.map((amt) => ({ value: amt, label: String(amt) }))
          : [];
        const conditionsList = Array.isArray(conditionJson?.conditions)
          ? conditionJson.conditions.map((c) => ({ value: c, label: c }))
          : [];
        const medsList = Array.isArray(medicationJson?.medications)
          ? medicationJson.medications.map((m) => ({ value: m, label: m }))
          : [];
        setCoverageOptions(coverage);
        setConditionOptions(conditionsList);
        setMedicationOptions(medsList);
      } catch (error) {
        console.error('Error loading dropdown data:', error);
      }
    };

    fetchOptions();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleFieldChange = useCallback((field) => (value) => {
    const nextValue = value?.target ? value.target.value : value;
    setForm((prev) => ({ ...prev, [field]: nextValue }));
  }, []);

  const handleHeightInchesChange = useCallback((value) => {
    const nextValue = value?.target ? value.target.value : value;
    setForm((prev) => ({ ...prev, heightInches: sanitizeInches(nextValue) }));
  }, []);

  const handleAddCondition = useCallback(() => {
    if (!pendingCondition) return;
    const id = `${pendingCondition}-${Date.now()}`;
    setConditions((prev) => [
      ...prev,
      {
        id,
        condition: pendingCondition,
        criteria: '',
        hasCriteria: false,
        criteriaOptions: [],
        loadingCriteria: true,
      },
    ]);
    setPendingCondition('');

    fetch(ENDPOINTS.criteria, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ condition: pendingCondition }),
    })
      .then((response) => response.json())
      .then((data) => {
        const criteria = Array.isArray(data?.criteria) ? data.criteria : [];
        setConditions((prev) =>
          prev.map((item) =>
            item.id === id
              ? {
                  ...item,
                  criteriaOptions: criteria.map((crit) => ({ value: crit, label: crit })),
                  hasCriteria: criteria.length > 0,
                  loadingCriteria: false,
                }
              : item
          )
        );
      })
      .catch((error) => {
        console.error('Error fetching criteria:', error);
        setConditions((prev) =>
          prev.map((item) =>
            item.id === id
              ? {
                  ...item,
                  criteriaOptions: [],
                  hasCriteria: false,
                  loadingCriteria: false,
                }
              : item
          )
        );
      });
  }, [pendingCondition]);

  const handleAddMedication = useCallback(() => {
    if (!pendingMedication) return;
    setMedications((prev) => [...prev, pendingMedication]);
    setPendingMedication('');
  }, [pendingMedication]);

  const handleReset = useCallback(() => {
    setForm(defaultFormState);
    setPendingCondition('');
    setPendingMedication('');
    setConditions([]);
    setMedications([]);
    setPremiumData([]);
    setMissingFields([]);
    setErrorMessage('');
  }, []);

  const validateForm = useCallback(() => {
    const requiredFields = [
      'coverageAmount',
      'gender',
      'state',
      'dob',
      'heightFeet',
      'heightInches',
      'weight',
      'nicotineUse',
    ];
    const missing = requiredFields.filter((field) => {
      const value = String(form[field] ?? '').trim();
      return value === '';
    });
    setMissingFields(missing);
    if (missing.length > 0) {
      showModal('missing', 'Please fill in all required fields.');
      return false;
    }

    if (String(pendingCondition || '').trim() || String(pendingMedication || '').trim()) {
      showModal(
        'unadded',
        "If you have a value selected for medication or conditions, please click 'Add' to proceed."
      );
      return false;
    }

    const missingCriteria = conditions.find(
      (condition) => condition.hasCriteria && !String(condition.criteria || '').trim()
    );
    if (missingCriteria) {
      showModal('criteria', `Please select criteria for condition: ${missingCriteria.condition}`);
      return false;
    }
    return true;
  }, [conditions, form, pendingCondition, pendingMedication, showModal]);

  const handleSubmit = useCallback(
    async (event) => {
      if (event?.preventDefault) event.preventDefault();
      setErrorMessage('');
      if (!validateForm()) return;

      const payload = {
        coverageAmount: form.coverageAmount,
        desiredPremium: form.desiredPremium,
        gender: form.gender,
        state: form.state,
        dob: form.dob,
        heightFeet: form.heightFeet,
        heightInches: form.heightInches,
        weight: form.weight,
        nicotineUse: form.nicotineUse,
        healthConditions: conditions.map((condition) => ({
          condition: condition.condition,
          criteria: condition.hasCriteria ? condition.criteria : '',
        })),
        medications: medications,
      };

      setLoadingQuote(true);
      setPremiumData([]);
      try {
        const response = await fetch(ENDPOINTS.quote, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const result = await response.json();
        const results = Array.isArray(result?.premiumData) ? result.premiumData : [];
        setPremiumData(results);
      } catch (error) {
        console.error('Error submitting quote:', error);
        setErrorMessage('Unable to submit quote. Please try again.');
      } finally {
        setLoadingQuote(false);
      }
    },
    [conditions, form, medications, validateForm]
  );

  const coverageDisabled = Boolean(String(form.desiredPremium || '').trim());
  const premiumDisabled = Boolean(String(form.coverageAmount || '').trim());

  const mobileOptions = useMemo(
    () => ({
      coverage: coverageOptions.length
        ? coverageOptions
        : [{ value: '', label: 'Select a coverage amount' }],
      conditions: conditionOptions.length
        ? conditionOptions
        : [{ value: '', label: 'Select a condition' }],
      medications: medicationOptions.length
        ? medicationOptions
        : [{ value: '', label: 'Select a medication' }],
    }),
    [coverageOptions, conditionOptions, medicationOptions]
  );

  const renderPremiumHeader = (item) => {
    const hiddenCarriers = ['emc', 'corebridge'];
    const carrierValue = (item?.carrier || '').toString();
    const shouldHideCarrier = hiddenCarriers.some((h) => carrierValue.toLowerCase().includes(h));
    const carrierDisplay = shouldHideCarrier ? '' : carrierValue;
    return (
      <AntSpace align="center" wrap>
        {item?.logo ? (
          <img src={item.logo} alt="Plan Logo" style={{ maxHeight: 50 }} />
        ) : null}
        {carrierDisplay ? <span style={{ fontWeight: 600 }}>{carrierDisplay}</span> : null}
        {item?.plan ? <span style={{ fontWeight: 700 }}>{item.plan}</span> : null}
        {item?.monthly_premium === 'Check Carrier Portal' ? (
          <AntTag color="gold">Check Carrier Portal for Premium</AntTag>
        ) : item?.monthly_premium ? (
          <span>
            <strong>Premium:</strong> ${item.monthly_premium}
          </span>
        ) : null}
      </AntSpace>
    );
  };

  const renderDetailList = (item, isMobileView) => {
    const items = DETAIL_FIELDS.map((field) => {
      const raw = normalizeValue(item?.[field.key]);
      if (raw === null) return null;
      if (raw === 'Yes') {
        return {
          label: field.label,
          value: isMobileView ? (
            <AMTag color="success">Yes</AMTag>
          ) : (
            <AntTag color="green">Yes</AntTag>
          ),
        };
      }
      if (raw === 'No') {
        return { label: field.label, value: 'No' };
      }
      return { label: field.label, value: raw };
    }).filter(Boolean);

    if (isMobileView) {
      return (
        <AMList>
          {items.map((row) => (
            <AMList.Item key={row.label} description={row.value}>
              <strong>{row.label}</strong>
            </AMList.Item>
          ))}
        </AMList>
      );
    }

    return (
      <AntList>
        {items.map((row) => (
          <AntList.Item key={row.label}>
            <AntSpace wrap>
              <strong>{row.label}:</strong>
              <span>{row.value}</span>
            </AntSpace>
          </AntList.Item>
        ))}
      </AntList>
    );
  };

  const renderResults = () => {
    if (loadingQuote) {
      return (
        <AntSpace align="center">
          <AntSpin />
          <span>Loading...</span>
        </AntSpace>
      );
    }
    if (!premiumData.length) {
      return <AntTypography.Text>Not Eligible For Any Plan Based On Inputs!</AntTypography.Text>;
    }
    return (
      <AntCollapse
        accordion
        items={premiumData.map((item, index) => ({
          key: String(index),
          label: renderPremiumHeader(item),
          children: (
            <div>
              {renderDetailList(item, false)}
              {item?.app ? (
                <AntButton
                  type="primary"
                  href={item.app}
                  target="_blank"
                  style={{ marginTop: 12 }}
                >
                  E-APP
                </AntButton>
              ) : null}
            </div>
          ),
        }))}
      />
    );
  };

  const renderMobileResults = () => {
    if (loadingQuote) {
      return (
        <AMSpace align="center" style={{ fontSize: 14 }}>
          <AMSpin />
          <span>Loading...</span>
        </AMSpace>
      );
    }
    if (!premiumData.length) {
      return <div>Not Eligible For Any Plan Based On Inputs!</div>;
    }
    return (
      <AMCollapse>
        {premiumData.map((item, index) => (
          <AMCollapse.Panel key={String(index)} title={renderPremiumHeader(item)}>
            {renderDetailList(item, true)}
            {item?.app ? (
              <AMButton
                block
                color="success"
                onClick={() => window.open(item.app, '_blank')}
                style={{ marginTop: 12 }}
              >
                E-APP
              </AMButton>
            ) : null}
          </AMCollapse.Panel>
        ))}
      </AMCollapse>
    );
  };

  const header = isMobile ? (
    <AMCard>
      <AMCard.Body>
        <AMSpace align="center" style={{ fontSize: 18, fontWeight: 700 }}>
          <span>Life Insurance Quote Tool v3</span>
        </AMSpace>
      </AMCard.Body>
    </AMCard>
  ) : (
    <AntCard
      style={{ marginBottom: 16 }}
      styles={{ body: { display: 'flex', alignItems: 'center' } }}
    >
      <Typography variant="h5" sx={{ fontWeight: 700, color: '#555' }}>
        Life Insurance Quote Tool v3
      </Typography>
    </AntCard>
  );

  const warningMessage = (
    <span>
      Premium Amounts Displayed Are Calculated Based On Rate Data Available.
      <br />
      Click The{' '}
      <AntButton size="small" type="primary" disabled style={{ margin: '0 4px' }}>
        E-APP
      </AntButton>
      Button In Each Policy To Visit The Carrier Portal To Verify!
    </span>
  );

  const formContent = (
    <AntSpace direction="vertical" size="middle" style={{ width: '100%' }}>
      <AntRow gutter={[16, 16]}>
        <AntCol xs={24} md={12}>
          <AntTypography.Text strong>Coverage Amount ($)</AntTypography.Text>
          <AntSelect
            placeholder="Select a coverage amount"
            value={form.coverageAmount || undefined}
            onChange={(value) => setForm((prev) => ({ ...prev, coverageAmount: value }))}
            options={coverageOptions}
            disabled={coverageDisabled}
            status={missingFields.includes('coverageAmount') ? 'error' : undefined}
            style={{ width: '100%' }}
          />
        </AntCol>
        <AntCol xs={24} md={12} style={{ display: 'none' }}>
          <AntTypography.Text strong>Desired Premium ($)</AntTypography.Text>
          <AntInput
            type="number"
            placeholder="Enter desired premium"
            value={form.desiredPremium}
            onChange={handleFieldChange('desiredPremium')}
            disabled={premiumDisabled}
          />
        </AntCol>
      </AntRow>

      <div>
        <AntTypography.Text strong>Gender</AntTypography.Text>
        <AntSelect
          placeholder="Select Gender"
          value={form.gender || undefined}
          onChange={(value) => setForm((prev) => ({ ...prev, gender: value }))}
          options={[
            { value: 'm', label: 'Male' },
            { value: 'f', label: 'Female' },
          ]}
          status={missingFields.includes('gender') ? 'error' : undefined}
          style={{ width: '100%' }}
        />
      </div>

      <div>
        <AntTypography.Text strong>State</AntTypography.Text>
        <AntSelect
          placeholder="Select State"
          value={form.state || undefined}
          onChange={(value) => setForm((prev) => ({ ...prev, state: value }))}
          options={STATE_OPTIONS}
          showSearch
          optionFilterProp="label"
          status={missingFields.includes('state') ? 'error' : undefined}
          style={{ width: '100%' }}
        />
      </div>

      <div>
        <AntTypography.Text strong>Date of Birth</AntTypography.Text>
        <AntInput
          type="date"
          value={form.dob}
          onChange={handleFieldChange('dob')}
          status={missingFields.includes('dob') ? 'error' : undefined}
        />
      </div>

      <AntRow gutter={[16, 16]}>
        <AntCol xs={24} md={12}>
          <AntTypography.Text strong>Height (ft/in)</AntTypography.Text>
          <AntSpace align="center">
            <AntInput
              type="number"
              placeholder="Feet"
              min={4}
              max={7}
              value={form.heightFeet}
              onChange={handleFieldChange('heightFeet')}
              status={missingFields.includes('heightFeet') ? 'error' : undefined}
              style={{ width: 90 }}
            />
            <span>ft</span>
            <AntInput
              type="number"
              placeholder="Inches"
              min={0}
              max={11}
              value={form.heightInches}
              onChange={handleHeightInchesChange}
              status={missingFields.includes('heightInches') ? 'error' : undefined}
              style={{ width: 90 }}
            />
            <span>in</span>
          </AntSpace>
        </AntCol>
        <AntCol xs={24} md={12}>
          <AntTypography.Text strong>Weight (lbs)</AntTypography.Text>
          <AntInput
            type="number"
            placeholder="Enter weight in lbs"
            value={form.weight}
            onChange={handleFieldChange('weight')}
            status={missingFields.includes('weight') ? 'error' : undefined}
          />
        </AntCol>
      </AntRow>

      <div>
        <AntTypography.Text strong>Nicotine Use</AntTypography.Text>
        <AntSelect
          placeholder="Select an option"
          value={form.nicotineUse || undefined}
          onChange={(value) => setForm((prev) => ({ ...prev, nicotineUse: value }))}
          options={[
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
          ]}
          status={missingFields.includes('nicotineUse') ? 'error' : undefined}
          style={{ width: '100%' }}
        />
      </div>

      <div>
        <AntTypography.Text strong>Health Conditions</AntTypography.Text>
        <AntSpace.Compact style={{ width: '100%' }}>
          <AntSelect
            placeholder="Select a condition"
            value={pendingCondition || undefined}
            onChange={(value) => setPendingCondition(value)}
            options={conditionOptions}
            style={{ width: '100%' }}
          />
          <AntButton onClick={handleAddCondition}>Add</AntButton>
        </AntSpace.Compact>
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {conditions.map((condition) => (
            <AntSpace key={condition.id} align="center" wrap>
              <AntTag
                closable
                onClose={(e) => {
                  e.preventDefault();
                  setConditions((prev) => prev.filter((item) => item.id !== condition.id));
                }}
              >
                {condition.condition}
              </AntTag>
              {condition.loadingCriteria ? <AntSpin size="small" /> : null}
              {condition.hasCriteria && !condition.loadingCriteria ? (
                <AntSelect
                  placeholder="Select criteria"
                  value={condition.criteria || undefined}
                  onChange={(value) =>
                    setConditions((prev) =>
                      prev.map((item) =>
                        item.id === condition.id ? { ...item, criteria: value } : item
                      )
                    )
                  }
                  options={condition.criteriaOptions}
                  style={{ minWidth: 220 }}
                />
              ) : null}
            </AntSpace>
          ))}
        </div>
      </div>

      <div>
        <AntTypography.Text strong>Medication</AntTypography.Text>
        <AntSpace.Compact style={{ width: '100%' }}>
          <AntSelect
            placeholder="Select a medication"
            value={pendingMedication || undefined}
            onChange={(value) => setPendingMedication(value)}
            options={medicationOptions}
            style={{ width: '100%' }}
          />
          <AntButton onClick={handleAddMedication}>Add</AntButton>
        </AntSpace.Compact>
        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {medications.map((medication, index) => (
            <AntTag
              key={`${medication}-${index}`}
              closable
              onClose={(e) => {
                e.preventDefault();
                setMedications((prev) => prev.filter((item, idx) => idx !== index));
              }}
            >
              {medication}
            </AntTag>
          ))}
        </div>
      </div>

      <AntSpace>
        <AntButton type="primary" onClick={handleSubmit}>
          Submit
        </AntButton>
        <AntButton onClick={handleReset}>Reset</AntButton>
      </AntSpace>
    </AntSpace>
  );

  const mobileForm = (
    <AMCard>
      <AMCard.Body>
        <AMSpace direction="vertical" block style={{ '--gap': '12px' }}>
          <MobileSelect
            label="Coverage Amount ($)"
            value={form.coverageAmount}
            onChange={(value) => setForm((prev) => ({ ...prev, coverageAmount: value }))}
            options={mobileOptions.coverage}
            placeholder="Select a coverage amount"
            disabled={coverageDisabled}
          />
          <MobileSelect
            label="Gender"
            value={form.gender}
            onChange={(value) => setForm((prev) => ({ ...prev, gender: value }))}
            options={[
              { value: 'm', label: 'Male' },
              { value: 'f', label: 'Female' },
            ]}
            placeholder="Select gender"
          />
          <MobileSelect
            label="State"
            value={form.state}
            onChange={(value) => setForm((prev) => ({ ...prev, state: value }))}
            options={STATE_OPTIONS}
            placeholder="Select state"
          />
          <AMList.Item>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Date of Birth</div>
            <AMInput
              type="date"
              value={form.dob}
              onChange={handleFieldChange('dob')}
            />
          </AMList.Item>
          <AMList.Item>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Height (ft/in)</div>
            <AMSpace align="center">
              <AMInput
                type="number"
                placeholder="Feet"
                value={form.heightFeet}
                onChange={handleFieldChange('heightFeet')}
                style={{ '--font-size': '14px' }}
              />
              <span>ft</span>
              <AMInput
                type="number"
                placeholder="Inches"
                value={form.heightInches}
                onChange={handleHeightInchesChange}
                style={{ '--font-size': '14px' }}
              />
              <span>in</span>
            </AMSpace>
          </AMList.Item>
          <AMList.Item>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Weight (lbs)</div>
            <AMInput
              type="number"
              placeholder="Enter weight in lbs"
              value={form.weight}
              onChange={handleFieldChange('weight')}
            />
          </AMList.Item>
          <MobileSelect
            label="Nicotine Use"
            value={form.nicotineUse}
            onChange={(value) => setForm((prev) => ({ ...prev, nicotineUse: value }))}
            options={[
              { value: 'yes', label: 'Yes' },
              { value: 'no', label: 'No' },
            ]}
            placeholder="Select an option"
          />

          <AMList.Item>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Health Conditions</div>
            <MobileSelect
              label="Condition"
              value={pendingCondition}
              onChange={setPendingCondition}
              options={mobileOptions.conditions}
              placeholder="Select a condition"
            />
            <AMButton
              block
              size="small"
              onClick={handleAddCondition}
              style={{ marginTop: 8 }}
            >
              Add
            </AMButton>
            <AMSpace direction="vertical" block style={{ '--gap': '8px', marginTop: 8 }}>
              {conditions.map((condition) => (
                <AMSpace key={condition.id} align="center" wrap>
                  <AMTag
                    closeable
                    onClose={() =>
                      setConditions((prev) => prev.filter((item) => item.id !== condition.id))
                    }
                  >
                    {condition.condition}
                  </AMTag>
                  {condition.loadingCriteria ? <AMSpin /> : null}
                  {condition.hasCriteria && !condition.loadingCriteria ? (
                    <MobileSelect
                      label="Criteria"
                      value={condition.criteria}
                      onChange={(value) =>
                        setConditions((prev) =>
                          prev.map((item) =>
                            item.id === condition.id ? { ...item, criteria: value } : item
                          )
                        )
                      }
                      options={condition.criteriaOptions}
                      placeholder="Select criteria"
                    />
                  ) : null}
                </AMSpace>
              ))}
            </AMSpace>
          </AMList.Item>

          <AMList.Item>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Medication</div>
            <MobileSelect
              label="Medication"
              value={pendingMedication}
              onChange={setPendingMedication}
              options={mobileOptions.medications}
              placeholder="Select a medication"
            />
            <AMButton
              block
              size="small"
              onClick={handleAddMedication}
              style={{ marginTop: 8 }}
            >
              Add
            </AMButton>
            <AMSpace direction="vertical" block style={{ '--gap': '8px', marginTop: 8 }}>
              {medications.map((medication, index) => (
                <AMTag
                  key={`${medication}-${index}`}
                  closeable
                  onClose={() =>
                    setMedications((prev) => prev.filter((item, idx) => idx !== index))
                  }
                >
                  {medication}
                </AMTag>
              ))}
            </AMSpace>
          </AMList.Item>

          <AMSpace block>
            <AMButton block color="primary" onClick={handleSubmit}>
              Submit
            </AMButton>
            <AMButton block onClick={handleReset}>
              Reset
            </AMButton>
          </AMSpace>
        </AMSpace>
      </AMCard.Body>
    </AMCard>
  );

  return (
    <Box>
      {header}

      {isMobile ? (
        <AMCard>
          <AMCard.Body>
            <AMSpace align="center" style={{ fontWeight: 600 }}>
              <AMTag color="warning">!</AMTag>
              <span>
                Premium Amounts Displayed Are Calculated Based On Rate Data Available.
                <br />
                Click The{' '}
                <AMButton size="mini" disabled color="success">
                  E-APP
                </AMButton>{' '}
                Button In Each Policy To Visit The Carrier Portal To Verify!
              </span>
            </AMSpace>
          </AMCard.Body>
        </AMCard>
      ) : (
        <AntAlert
          type="warning"
          showIcon
          message={warningMessage}
          style={{ marginBottom: 16 }}
        />
      )}

      {errorMessage ? (
        <AntAlert
          type="error"
          showIcon
          message={errorMessage}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {isMobile ? (
        <AMSpace direction="vertical" block style={{ '--gap': '16px' }}>
          {mobileForm}
          <AMCard>
            <AMCard.Body>
              <AMSpace direction="vertical" block style={{ '--gap': '12px' }}>
                <div style={{ fontWeight: 700 }}>Quote Results</div>
                {renderMobileResults()}
              </AMSpace>
            </AMCard.Body>
          </AMCard>
        </AMSpace>
      ) : (
        <AntRow gutter={[24, 24]}>
          <AntCol xs={24} md={12}>
            <AntCard>{formContent}</AntCard>
          </AntCol>
          <AntCol xs={24} md={12}>
            <AntCard title="Quote Results">{renderResults()}</AntCard>
          </AntCol>
        </AntRow>
      )}

      {isMobile ? (
        <AMPopup visible={Boolean(modal.type)} onMaskClick={closeModal} bodyStyle={{ padding: 16 }}>
          <AMSafeArea position="top" />
          <div style={{ fontWeight: 700, marginBottom: 8 }}>
            {modal.type === 'missing'
              ? 'Missing Required Fields'
              : modal.type === 'unadded'
                ? 'Incomplete Selection'
                : 'Missing Criteria'}
          </div>
          <div style={{ color: '#5a6474', marginBottom: 12 }}>{modal.message}</div>
          <AMButton block color="primary" onClick={closeModal}>
            Ok
          </AMButton>
          <AMSafeArea position="bottom" />
        </AMPopup>
      ) : (
        <AntModal
          open={Boolean(modal.type)}
          onCancel={closeModal}
          onOk={closeModal}
          title={
            modal.type === 'missing'
              ? 'Missing Required Fields'
              : modal.type === 'unadded'
                ? 'Incomplete Selection'
                : 'Missing Criteria'
          }
        >
          <p>{modal.message}</p>
        </AntModal>
      )}
    </Box>
  );
};

export default LifeQuoteTool;
